import { useEffect, useState } from "react";
import { ShoppingBag, ShoppingCart, Search, Heart, User, LogOut, LogIn, Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getAuthUser, isAuthenticated, logout } from "../core/auth";
import { normalizeRole } from "../core/access";
import { useHeaderNav } from "../context/HeaderNavContext";
import TopNavMenu, { collectTopNavBinds } from "../ui/components/TopNavMenu";
import JsonMarquee from "../ui/components/JsonMarquee";
import { getV, setV } from "../core/binding";
import { runRules } from "../core/ruleEngine";
import { clearSessionScopedUI, getScreen, sub } from "../core/state";
import { getCartCount, subCart } from "../core/cart";

export default function Header() {
  const productFilters = getScreen("products")?.filters || {};
  const hasActiveProductFilters = Object.values(productFilters).some((value) => {
    if (value === undefined || value === null) return false;
    const v = String(value).trim();
    if (!v) return false;
    return v.toLowerCase() !== "all";
  });

  const navigate = useNavigate();
  const location = useLocation();
  const authUser = getAuthUser();
  const loggedIn = isAuthenticated();
  const normalizedRole = normalizeRole(authUser?.role);
  const isVendorLike = normalizedRole === "vendor";
  const roleLabel = isVendorLike ? "Vendor" : "Shopper";
  const roleMark = roleLabel === "Vendor" ? "◆" : "★";
  const chipName = String(authUser?.name || authUser?.email || "User").trim();
  const { headerNav } = useHeaderNav();
  const [, bump] = useState(0);
  const [cartCount, setCartCount] = useState(() => getCartCount());
  const [mobileBrowseOpen, setMobileBrowseOpen] = useState(false);
  const [narrowHeader, setNarrowHeader] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches
  );

  useEffect(() => sub(() => bump((n) => n + 1)), []);
  useEffect(() => subCart(() => setCartCount(getCartCount())), []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const sync = () => setNarrowHeader(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (!mobileBrowseOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileBrowseOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileBrowseOpen]);

  useEffect(() => {
    setMobileBrowseOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    clearSessionScopedUI();
    navigate("/", { replace: true });
  };

  const control = headerNav?.control;
  const sectionId = headerNav?.sectionId;
  const rules = headerNav?.rules || [];
  const marqueeConfig = headerNav?.marquee || null;

  const handleTopNavReset = () => {
    if (!control) return;
    collectTopNavBinds(control).forEach((bind) => setV(bind, ""));
    if (rules.length && sectionId) runRules(rules, sectionId);
  };
  const handleSearchClick = () => {
    setMobileBrowseOpen(false);
    if (location.pathname !== "/products") {
      navigate("/products");
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent("lfx-expand-shop-filters"));
      }, 120);
    } else {
      window.dispatchEvent(new CustomEvent("lfx-expand-shop-filters"));
    }
  };

  const renderTopNav = (inHeader) =>
    control ? (
      <TopNavMenu
        key={inHeader ? "topnav-desktop" : "topnav-mobile"}
        inHeader={inHeader}
        variant={control.variant || "simple"}
        items={control.items || []}
        groups={control.groups || []}
        theme={control.theme || ""}
        openOn={control.openOn ?? "hover"}
        getValue={(bind) => getV(bind)}
        onItemChange={(item, v) => {
          if (!item?.bind) return;
          setV(item.bind, v);
          if (rules.length && sectionId) runRules(rules, sectionId);
        }}
        onReset={handleTopNavReset}
        onResetAll={handleTopNavReset}
        showReset={control.showReset !== false}
        resetLabel={control.resetLabel || "Reset"}
        resetScope={control.resetScope || "nav"}
        onMegaNavigate={inHeader ? undefined : () => setMobileBrowseOpen(false)}
      />
    ) : null;

  return (
    <header className="lfx-header">
      <div className="lfx-header-row">
        <Link to="/" className="lfx-header-brand lfx-header-brand-link">
          <ShoppingBag size={22} className="lfx-brand-icon" />
          <span className="lfx-brand-name">NISTTA</span>
          <span className="lfx-brand-tagline">Fine Indian Sarees</span>
        </Link>

        <div className="lfx-header-nav">{!narrowHeader ? renderTopNav(true) : null}</div>

        {narrowHeader && control ? (
          <button
            type="button"
            className="lfx-icon-btn lfx-header-browse-btn"
            title="Shop & collections"
            aria-expanded={mobileBrowseOpen}
            onClick={() => setMobileBrowseOpen((o) => !o)}
          >
            <Menu size={18} />
          </button>
        ) : null}

        <div className="lfx-header-actions">
          <button
            className={`lfx-icon-btn ${hasActiveProductFilters ? "lfx-icon-btn--with-indicator" : ""}`}
            title={hasActiveProductFilters ? "Search & filters (filters active)" : "Search & filters"}
            onClick={handleSearchClick}
          >
            <Search size={18} />
            {hasActiveProductFilters ? <span className="lfx-filter-active-indicator" aria-hidden>★</span> : null}
          </button>
          <Link className="lfx-header-cart" to="/cart" title="Shopping cart">
            <ShoppingCart size={18} />
            {cartCount > 0 ? (
              <span className="lfx-header-cart-badge">{cartCount > 99 ? "99+" : cartCount}</span>
            ) : null}
          </Link>
          {loggedIn ? (
            isVendorLike ? (
              <Link
                className="lfx-icon-btn lfx-icon-btn--with-indicator"
                to="/profile"
                title="Vendor profile"
              >
                <User size={18} />
                <span className="lfx-vendor-profile-star" aria-hidden>
                  ★
                </span>
              </Link>
            ) : (
              <Link className="lfx-icon-btn" to="/favorites" title="Favorite Items">
                <Heart size={18} />
              </Link>
            )
          ) : (
            <button className="lfx-icon-btn" title="Wishlist">
              <Heart size={18} />
            </button>
          )}
          {loggedIn ? (
            <>
              <button
                type="button"
                className="lfx-auth-chip"
                title="Profile"
                onClick={() => navigate("/profile")}
              >
                <User size={14} />
                <span className="lfx-auth-chip-name">{chipName}</span>
                <span className={`lfx-auth-chip-role lfx-auth-chip-role--${roleLabel.toLowerCase()}`}>
                  {roleMark} {roleLabel}
                </span>
              </button>
              <button className="lfx-auth-link" onClick={handleLogout} title="Logout">
                <LogOut size={15} />
                Logout
              </button>
            </>
          ) : (
            <Link className="lfx-auth-link" to="/login" title="Login">
              <LogIn size={15} />
              Login
            </Link>
          )}
        </div>
      </div>
      <JsonMarquee config={marqueeConfig} />

      {narrowHeader && mobileBrowseOpen && control ? (
        <>
          <button
            type="button"
            className="lfx-mobile-browse-backdrop"
            aria-label="Close menu"
            onClick={() => setMobileBrowseOpen(false)}
          />
          <div className="lfx-mobile-browse-sheet" role="dialog" aria-modal="true" aria-label="Browse shop">
            <div className="lfx-mobile-browse-sheet-head">
              <span>Shop & collections</span>
              <button type="button" className="lfx-icon-btn lfx-mobile-browse-close" onClick={() => setMobileBrowseOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="lfx-mobile-browse-body">{renderTopNav(false)}</div>
          </div>
        </>
      ) : null}
    </header>
  );
}
